import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Alert,
  Chip,
  Stack,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { exploreApi } from "../services/rag-service";
import TemplateCard from "./TemplateCard";
import TemplateSkeletonCard from "./skeletons/TemplateSkeletonCard";

// The Explore tab's gallery view, mounted from Flows.jsx's activeTab === 2
// branch. Structurally mirrors DocumentCollectionsPage.jsx (Grid + skeleton
// array loading state) - the only real gallery-grid precedent in this
// codebase - and Flows.jsx's own search bar (same TextField+InputAdornment
// shape) for visual consistency with its Flows/Runs siblings.
//
// Search (name/summary) and category filtering are both client-side, against
// the whole already-fetched list (the backend cache never exceeds ~420
// templates - see app/api/v1/omniflow/explore.py). No chip-toggle filter
// precedent existed elsewhere in this codebase, so this is a small
// purpose-built one rather than a heavier dropdown Select - categories are a
// short flat list, well suited to a scannable chip row.
export function ExploreTemplates({ onTemplateClick }) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([exploreApi.listTemplates(), exploreApi.getCategories()])
      .then(([data, categoryData]) => {
        if (!cancelled) {
          const listedCategories = Array.isArray(categoryData) ? categoryData : [];
          // Match AP's category-based gallery, while retaining our single-flow
          // import restriction. Savings tags do not determine eligibility.
          const eligibleTemplates = (Array.isArray(data) ? data : []).filter(
            (template) =>
              !template.multiFlow &&
              template.categories?.some((category) =>
                listedCategories.includes(category),
              ),
          );
          setTemplates(eligibleTemplates);
          setCategories(listedCategories);
        }
      })
      .catch((err) => {
        console.error("Error fetching explore templates:", err);
        if (!cancelled) {
          setError(
            err.message || t("flows.failedToLoadTemplates"),
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const filteredTemplates = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return templates.filter((template) => {
      const matchesQuery =
        !query ||
        template.name?.toLowerCase().includes(query) ||
        template.summary?.toLowerCase().includes(query);
      const matchesCategory =
        !selectedCategory || template.categories?.includes(selectedCategory);
      return matchesQuery && matchesCategory;
    });
  }, [templates, searchTerm, selectedCategory]);

  return (
    <>
      <Box sx={{ mb: 3, maxWidth: 400 }}>
        <TextField
          placeholder={t("flows.searchTemplatesPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {categories.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 3, flexWrap: "wrap", rowGap: 1 }}
        >
          <Chip
            label={t("flows.allCategories")}
            size="small"
            color={selectedCategory === null ? "primary" : "default"}
            variant={selectedCategory === null ? "filled" : "outlined"}
            onClick={() => setSelectedCategory(null)}
          />
          {categories.map((category) => (
            <Chip
              key={category}
              label={category}
              size="small"
              color={selectedCategory === category ? "primary" : "default"}
              variant={selectedCategory === category ? "filled" : "outlined"}
              onClick={() =>
                setSelectedCategory((prev) =>
                  prev === category ? null : category,
                )
              }
            />
          ))}
        </Stack>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {loading ? (
          Array.from({ length: 8 }).map((_, index) => (
            <Grid
              item xs={12} sm={6} md={4} lg={3}
              key={`skeleton-${index}`}
              sx={{ display: "flex" }}
            >
              <TemplateSkeletonCard />
            </Grid>
          ))
        ) : filteredTemplates.length > 0 ? (
          filteredTemplates.map((template) => (
            <Grid
              item xs={12} sm={6} md={4} lg={3}
              key={template.id}
              sx={{ display: "flex" }}
            >
              <TemplateCard template={template} onClick={onTemplateClick} />
            </Grid>
          ))
        ) : !error ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                {t("flows.noTemplatesFound")}
              </Typography>
            </Box>
          </Grid>
        ) : null}
      </Grid>
    </>
  );
}
